import { useApolloClient } from '@apollo/client/index.js';
import {
  useCreatePasswordMutation,
  useExchangeForLimitedJwtMutation,
  useLoginMutation,
  useLoginWithGoogleMutation,
  useRecoverPasswordMutation,
  useResetPasswordMutation,
  useSignupPatientMutation,
} from '@pm/graphql';
import { TokenResponse } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { isPast, parse } from 'date-fns';
import {
  FunctionComponent,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useEnvironment } from '../../hooks/env';
import {
  signInWithGoogle,
  signOutFromGoogle,
} from '../../providers/GoogleSsoProvider';
import { signOutOfZendesk } from '../../utilities/zendeskMessenger';
import { AuthContext } from './context';

const storeToken = (token: string) => {
  // save our jwt token into session storage in case the page is refreshed
  localStorage.setItem('jwt', token);
};

export const parseJwt = (jwt: string) => {
  const base64Payload = jwt.split('.')[1];
  const payload: { sub?: number; exp?: number } = JSON.parse(
    atob(base64Payload),
  );

  return payload;
};

export const AuthProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const client = useApolloClient();
  const { googleAuthUrl } = useEnvironment();
  const navigate = useNavigate();
  const [jwtToken, setJwtToken] = useState<string>(() => {
    const rawToken = localStorage.getItem('jwt');

    // if we don't have a token bail out
    if (!rawToken) {
      return '';
    }
    const parsedToken = parseJwt(rawToken);

    if (!parsedToken.exp) {
      localStorage.removeItem('jwt');
      return '';
    }

    // see if it's expired
    if (isPast(parse(parsedToken.exp.toString(), 't', new Date()))) {
      localStorage.removeItem('jwt');
      return '';
    }

    // our token is valid
    return rawToken;
  });
  const [error, setError] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle('authenticated', !!jwtToken);
  }, [jwtToken]);

  const userId = useMemo(() => {
    if (!jwtToken) {
      return undefined;
    }

    const parsedToken = parseJwt(jwtToken);

    if (!parsedToken.sub) {
      return undefined;
    }

    return parsedToken.sub.toString();
  }, [jwtToken]);

  const anonymousUserId = useMemo(() => {
    const currentAnonymousId =
      JSON.parse(localStorage.getItem('ajs_anonymous_id') || 'null') ||
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('ajs_anonymous_id='))
        ?.split('=')[1];

    if (!currentAnonymousId) {
      const newAnonymousId: string = crypto.randomUUID();
      localStorage.setItem('ajs_anonymous_id', JSON.stringify(newAnonymousId));
      return newAnonymousId;
    }

    return currentAnonymousId;
  }, []);

  const [loginWithGoogleMutation] = useLoginWithGoogleMutation();

  const useLogin = () =>
    useLoginMutation({
      onCompleted: ({ login }) => {
        if (!login) {
          return;
        }

        const { jwt, errors } = login;

        if (errors.length) {
          return setError(true);
        }
        if (jwt) {
          setJwtToken(jwt);
          storeToken(jwt);
        }
      },
      // bug: some errors aren't being caught inside onError using mutations.
      // network errors are being caught in onError.
      // https://github.com/apollographql/apollo-client/issues/5708
      onError: (error) => {
        setError(true);
        throw error;
      },
    });

  const providerLogin = async (oauth_response: TokenResponse) => {
    const form = new FormData();
    form.append('access_token', oauth_response.access_token);
    const response = await fetch(googleAuthUrl, { method: 'POST', body: form });

    if (response.ok) {
      const jsonValue = await response.json();

      setJwtToken(jsonValue.jwt);
      storeToken(jsonValue.jwt);
    } else {
      setError(true);
    }
  };

  const patientGoogleLogin = async (): Promise<
    'logged_in' | 'redirect_to_intake' | 'cancelled' | 'network_error' | 'error'
  > => {
    setError(false);
    try {
      const firebaseIdToken = await signInWithGoogle();
      const { data } = await loginWithGoogleMutation({
        variables: { input: { firebaseIdToken } },
      });
      const result = data?.loginWithGoogle;
      if (!result) {
        setError(true);
        return 'error';
      }
      if (result.redirectToIntake) {
        // Best-effort — a failed sign-out is not fatal; swallow so we still
        // return redirect_to_intake and let the caller navigate away.
        try {
          await signOutFromGoogle();
        } catch {
          void 0; // best-effort — a failed sign-out is not fatal
        }
        return 'redirect_to_intake';
      }
      if (result.jwt) {
        setJwtToken(result.jwt);
        storeToken(result.jwt);
        return 'logged_in';
      }
      setError(true);
      return 'error';
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        // window.open() returned null. Two different causes share this one
        // code and we can't tell them apart here: (1) a timing race — Firebase
        // awaits a network call before calling window.open(), so on a slow
        // connection the browser's user-gesture window can lapse and refuse a
        // genuine click, where a retry usually succeeds; (2) a persistent
        // block — a real popup-blocker setting or an in-app browser/webview
        // that disallows window.open() entirely, where every retry fails the
        // same way. We treat both as non-actionable rather than paging on the
        // unrecoverable case, which we can't distinguish from the transient one.
        code === 'auth/popup-blocked' ||
        // The user declined permissions on Google's own consent screen.
        code === 'auth/user-cancelled'
      ) {
        return 'cancelled';
      }
      if (code === 'auth/network-request-failed') {
        return 'network_error';
      }
      Sentry.captureException(e);
      setError(true);
      return 'error';
    }
  };

  const useSignup = (login = true) =>
    useSignupPatientMutation({
      onCompleted: ({ signupPatient }) => {
        if (!signupPatient) {
          return;
        }
        const { jwt, patient, errors } = signupPatient;

        if (errors?.length) {
          setError(true);
          throw errors[0];
        }
        if (login && jwt) {
          setJwtToken(jwt);
          storeToken(jwt);
        }
        return patient;
      },
      // bug: some errors aren't being caught inside onError using mutations.
      // network errors are being caught in onError.
      // https://github.com/apollographql/apollo-client/issues/5708
      onError: (error) => {
        setError(true);
        throw error;
      },
    });

  const logout = () => {
    setJwtToken('');
    localStorage.removeItem('jwt');
    localStorage.removeItem('email');
    localStorage.removeItem('PROVINCE');
    localStorage.removeItem('COUNTRY');
    sessionStorage.clear();
    client.clearStore();
    //@ts-expect-error - this method comes from index.html, loads OneTrust and analytics scripts
    if (loadAnalytics) loadAnalytics();
    signOutOfZendesk();
    return navigate('/', { replace: true });
  };

  const useResetPassword = () =>
    useResetPasswordMutation({
      onCompleted: ({ resetPassword }) => {
        if (!resetPassword) {
          return;
        }

        const { errors } = resetPassword;

        if (errors.length) {
          setError(true);
        }
      },
    });

  const useRecoverPassword = () =>
    useRecoverPasswordMutation({
      onCompleted: ({ recoverPassword }) => {
        if (!recoverPassword) {
          return;
        }

        const { errors } = recoverPassword;

        if (errors.length) {
          setError(true);
        }
      },
    });

  const useCreatePassword = () =>
    useCreatePasswordMutation({
      onCompleted: ({ createPassword }) => {
        if (!createPassword) {
          return;
        }

        const { errors } = createPassword;

        if (errors.length) {
          setError(true);
        }
      },
    });
  const useExchangeForLimitedJwt = () =>
    useExchangeForLimitedJwtMutation({
      onCompleted: ({ exchangeForLimitedJwt }) => {
        if (!exchangeForLimitedJwt) {
          return null;
        }

        const { infoGatheringJwt } = exchangeForLimitedJwt;

        if (!infoGatheringJwt) {
          setError(true);
          return null;
        }
        return infoGatheringJwt;
      },
    });

  return (
    <AuthContext
      value={{
        isLoggedIn: !!jwtToken,
        error,
        setError,
        providerLogin,
        patientGoogleLogin,
        logout,
        userId,
        anonymousUserId,
        useLogin,
        useSignup,
        useResetPassword,
        useRecoverPassword,
        useCreatePassword,
        useExchangeForLimitedJwt,
      }}
    >
      {children}
    </AuthContext>
  );
};
